import { useState } from 'react';
import { SaleOffer, BuyRequest } from '../models/Marketplace';
import { User } from '../models/User';

export interface UseMarketplaceModalsReturn {
  // Detail modals state
  selectedSaleOffer: SaleOffer | null;
  selectedBuyRequest: BuyRequest | null;
  
  // Message composer state
  showMessageComposer: boolean;
  selectedSeller: User | null;
  selectedBuyer: User | null;
  relatedEntityId: string | undefined;
  
  // Actions
  openSaleOfferDetails: (offer: SaleOffer) => void;
  openBuyRequestDetails: (request: BuyRequest) => void;
  closeSaleOfferDetails: () => void;
  closeBuyRequestDetails: () => void;
  
  // Message actions
  openMessageComposer: (seller?: User, buyer?: User, entityId?: string) => void;
  closeMessageComposer: () => void;
  resetMessageState: () => void;
  
  // Messaging handlers
  handleMessageSeller: (sellerName: string, offerId: string, allUsers: User[]) => void;
  handleMessageBuyer: (buyerName: string, requestId: string, allUsers: User[]) => void;
  handleMessageSend: (recipientId: string, content: string, relatedEntityId?: string) => void;
}

export const useMarketplaceModals = (): UseMarketplaceModalsReturn => {
  // Detail modals state
  const [selectedSaleOffer, setSelectedSaleOffer] = useState<SaleOffer | null>(null);
  const [selectedBuyRequest, setSelectedBuyRequest] = useState<BuyRequest | null>(null);
  
  // Message composer state
  const [showMessageComposer, setShowMessageComposer] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState<User | null>(null);
  const [selectedBuyer, setSelectedBuyer] = useState<User | null>(null);
  const [relatedEntityId, setRelatedEntityId] = useState<string | undefined>(undefined);

  // Detail modal actions
  const openSaleOfferDetails = (offer: SaleOffer) => {
    setSelectedSaleOffer(offer);
  };

  const openBuyRequestDetails = (request: BuyRequest) => {
    setSelectedBuyRequest(request);
  };

  const closeSaleOfferDetails = () => {
    setSelectedSaleOffer(null);
  };

  const closeBuyRequestDetails = () => {
    setSelectedBuyRequest(null);
  };

  // Message composer actions
  const openMessageComposer = (seller?: User, buyer?: User, entityId?: string) => {
    if (seller) setSelectedSeller(seller);
    if (buyer) setSelectedBuyer(buyer);
    if (entityId) setRelatedEntityId(entityId);
    setShowMessageComposer(true);
  };

  const closeMessageComposer = () => {
    setShowMessageComposer(false);
    resetMessageState();
  };

  const resetMessageState = () => {
    setSelectedSeller(null);
    setSelectedBuyer(null);
    setRelatedEntityId(undefined);
  };

  // Messaging handlers
  const handleMessageSeller = (sellerName: string, offerId: string, allUsers: User[]) => {
    const seller = allUsers.find(u => u.fullName === sellerName);
    if (seller) {
      setSelectedSeller(seller);
      setSelectedBuyer(null);
      setRelatedEntityId(offerId);
      setShowMessageComposer(true);
    }
  };

  const handleMessageBuyer = (buyerName: string, requestId: string, allUsers: User[]) => {
    const buyer = allUsers.find(u => u.fullName === buyerName);
    if (buyer) {
      setSelectedBuyer(buyer);
      setSelectedSeller(null);
      setRelatedEntityId(requestId);
      setShowMessageComposer(true);
    }
  };

  const handleMessageSend = (recipientId: string, content: string, relatedEntityId?: string) => {
    // This would typically integrate with a messaging service
          // Message sent successfully
    
    // Close the message composer after sending
    closeMessageComposer();
  };

  return {
    // Detail modals state
    selectedSaleOffer,
    selectedBuyRequest,
    
    // Message composer state
    showMessageComposer,
    selectedSeller,
    selectedBuyer,
    relatedEntityId,
    
    // Actions
    openSaleOfferDetails,
    openBuyRequestDetails,
    closeSaleOfferDetails,
    closeBuyRequestDetails,
    
    // Message actions
    openMessageComposer,
    closeMessageComposer,
    resetMessageState,
    
    // Messaging handlers
    handleMessageSeller,
    handleMessageBuyer,
    handleMessageSend
  };
}; 